/**
 * @fileoverview Mock backend for the password-recovery endpoints.
 * @description Registers POST /identity/password/forgot, /identity/password/reset/verify and
 * /identity/password/reset on the feature's shared mock adapter (see auth-mock-adapter.ts) so the
 * real request/response interceptors still run. Forgot always reports success, whether or not the
 * identifier is registered — matching the real backend's deliberate anti-account-enumeration
 * behavior. Verify and reset follow the backend's contract: every unusable token is the same 400 /
 * 1031, a weak new password is 400 / 1034, and a successful reset uses the token up. Fixture
 * tokens (open `/reset-password?token=<one>`): `valid-reset-token` (usable until a reset succeeds),
 * `expired-reset-token` (1031) and `rate-limited-reset-token` (429 / 1032). A no-op when
 * VITE_ENABLE_MOCK_API is false, so switching to a real backend is deleting the one import of this
 * file in features/auth/index.ts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { passwordMeetsPolicy } from '@/features/auth/utils/password-policy.utils'

import { authMockAdapter } from './auth-mock-adapter'
import { buildMockErrorEnvelope, buildMockSuccessEnvelope } from './mock-envelope.utils'
import { MOCK_PASSWORD_POLICY } from './password-policy-mock.service'

import type {
  PasswordResetResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  VerifyPasswordResetRequest,
  VerifyPasswordResetResponse,
} from '@/features/auth/services/password-reset.service'

const VALID_RESET_TOKEN = 'valid-reset-token'
const RATE_LIMITED_RESET_TOKEN = 'rate-limited-reset-token'

/** Tokens already used by a successful reset in this page session. */
const usedResetTokens = new Set<string>()

const TOKEN_INVALID_ERROR = [
  400,
  buildMockErrorEnvelope(400, '1031', 'Password reset token is invalid or no longer usable.'),
] as const

/**
 * @description Whether a fixture token is still usable.
 * @param {string} token - The presented reset token.
 * @returns {boolean} True only for the valid fixture token, until it's been used.
 */
function isUsableToken(token: string): boolean {
  return token === VALID_RESET_TOKEN && !usedResetTokens.has(token)
}

if (authMockAdapter) {
  authMockAdapter.onPost('/identity/password/forgot').reply(() => {
    const response: PasswordResetResponse = { requestAccepted: true }

    return [200, buildMockSuccessEnvelope(response, 'Password reset request accepted')]
  })

  authMockAdapter.onPost('/identity/password/reset/verify').reply((config) => {
    const { token } = JSON.parse(config.data as string) as VerifyPasswordResetRequest

    if (token === RATE_LIMITED_RESET_TOKEN) {
      return [
        429,
        buildMockErrorEnvelope(429, '1032', 'Password reset verification rate limit exceeded.'),
      ]
    }

    if (!isUsableToken(token)) {
      return [...TOKEN_INVALID_ERROR]
    }

    const response: VerifyPasswordResetResponse = { verified: true }
    return [200, buildMockSuccessEnvelope(response, 'Password reset token verified')]
  })

  authMockAdapter.onPost('/identity/password/reset').reply((config) => {
    const { token, newPassword, confirmPassword } = JSON.parse(
      config.data as string,
    ) as ResetPasswordRequest

    if (!isUsableToken(token)) {
      return [...TOKEN_INVALID_ERROR]
    }

    if (newPassword !== confirmPassword) {
      return [400, buildMockErrorEnvelope(400, '0001', 'Validation failed.')]
    }

    if (!passwordMeetsPolicy(MOCK_PASSWORD_POLICY, newPassword)) {
      return [
        400,
        buildMockErrorEnvelope(
          400,
          '1034',
          'Password does not meet policy or history requirements.',
        ),
      ]
    }

    usedResetTokens.add(token)
    const response: ResetPasswordResponse = { passwordReset: true, loginRequired: true }
    return [200, buildMockSuccessEnvelope(response, 'Password reset successfully completed')]
  })
}
